import React, { memo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Heart, Sparkles, Zap, Star } from "lucide-react";
import { useApp } from "@/context";
import ShareButton from "@/components/ShareButton";
import { BRAND } from "@/brand";
import { optimizeImg } from "@/lib/img";
import { useBnplHint } from "@/components/BnplBadge";

const EXPRESS_RE = /ساعة|express|same[\s-]?day|توصيل سريع|سريع/i;

function ProductCard({ product, onAdd }) {
  const { lang, formatPrice, t, addToCart, toggleFavorite, isFavorite, country } = useApp();
  const bnpl = useBnplHint(country);
  const name = lang === "ar" ? product.name_ar : product.name_en;
  const fav = isFavorite(product.id);
  const isCommunity = product.source === "user_design";

  const tags = Array.isArray(product.tags) ? product.tags : [];
  const hasDiscount = product.price_previous && Number(product.price_previous) > Number(product.price_sar);
  const discountPct = hasDiscount
    ? Math.round((1 - Number(product.price_sar) / Number(product.price_previous)) * 100)
    : 0;
  const isNew = !!product.is_new;
  const isBestseller = !!(product.bestseller || product.featured);
  const isExpress = !!(product.same_day || product.delivery_days_max === 0 || tags.some((x) => EXPRESS_RE.test(String(x))));
  const isBlocked = !!product.blocked;
  const isOut = product.stock_mode === "out"
    || (product.stock_mode === "limited" && Number(product.stock ?? 0) <= 0);
  const unavailable = isBlocked || isOut;
  // 🗓️ «قسّطها من X» — يظهر عند توفر مزوّد تقسيط للدولة والسعر ضمن الحدود
  // ⚠️ Tabby مستثناة: لا يوجد سنيبت رسمي بحجم بطاقة المنتج، والحساب اليدوي (÷4) مضلل —
  // رسائل تابي الرسمية تظهر في صفحة المنتج والسلة والدفع فقط
  const cardPrice = Number(product.price_sar) > 0 ? Number(product.price_sar) : Number(product.options_min_price) || 0;
  const showBnplHint = !!(bnpl?.provider && bnpl.provider !== "tabby" && !unavailable && cardPrice >= bnpl.min && cardPrice <= bnpl.max);

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (unavailable) return;
    addToCart(product, 1);
    onAdd && onAdd(product);
  };

  const handleFav = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite(product.id);
  };

  return (
    <motion.div whileHover={{ y: -3 }} transition={{ duration: 0.2 }}>
      <Link
        to={`/p/${product.id}`}
        className="card-soft block overflow-hidden"
        data-testid={`product-card-${product.id}`}
      >
        <div className="relative aspect-[4/5] overflow-hidden bg-gray-100">
          <img
            src={optimizeImg(product.image, 480) || "https://res.cloudinary.com/demo/image/upload/w_400,h_500,c_fill,q_auto:eco/v1/sample.jpg"}
            alt={name}
            className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
            style={unavailable ? { filter: "grayscale(0.85) brightness(0.92)" } : undefined}
            loading="lazy"
            decoding="async"
            width="400"
            height="500"
            onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
          />

          {/* ⛔ موقوف مؤقتاً — شريط واضح فوق الصورة */}
          {isBlocked && (
            <div className="absolute inset-x-0 bottom-0 py-1.5 text-center text-[11px] font-black text-white"
                 style={{ background: "rgba(180,83,9,0.92)", backdropFilter: "blur(2px)" }}
                 data-testid={`badge-blocked-${product.id}`}>
              {lang === "ar" ? "غير متوفر حالياً · سيعود قريباً" : "Currently unavailable · Back soon"}
            </div>
          )}

          {/* 🚫 نفد من المخزون — شريط أحمر فوق الصورة */}
          {!isBlocked && isOut && (
            <div className="absolute inset-x-0 bottom-0 py-1.5 text-center text-[11px] font-black text-white"
                 style={{ background: "rgba(185,28,28,0.92)", backdropFilter: "blur(2px)" }}
                 data-testid={`badge-oos-${product.id}`}>
              {lang === "ar" ? "نفد من المخزون" : "Out of stock"}
            </div>
          )}

          {/* Badges — top-start, stacked. Order: discount > bestseller > new > express */}
          <div className="absolute top-2.5 start-2.5 flex flex-col items-start gap-1 max-w-[70%]"
               data-testid={`product-badges-${product.id}`}>
            {hasDiscount && (
              <span className="rounded-full px-2 py-0.5 text-[10px] font-bold shadow-sm"
                    style={{ background: "#000", color: "#fff" }}
                    data-testid={`badge-discount-${product.id}`}>
                -{discountPct}%
              </span>
            )}
            {isCommunity ? (
              <span className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold backdrop-blur"
                    style={{ background: "rgba(0,0,0,0.9)", color: "#fff" }}
                    data-testid={`product-community-badge-${product.id}`}>
                <Sparkles size={10} /> {lang === "ar" ? "تصميم مجتمعنا" : "Community"}
              </span>
            ) : (
              <>
                {isBestseller && (
                  <span className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold shadow-sm border border-black/10"
                        style={{ background: "#fff", color: "#000" }}
                        data-testid={`badge-bestseller-${product.id}`}>
                    <Star size={10} fill="currentColor" /> {lang === "ar" ? "الأكثر مبيعاً" : "Bestseller"}
                  </span>
                )}
                {isNew && (
                  <span className="rounded-full px-2 py-0.5 text-[10px] font-bold shadow-sm"
                        style={{ background: "#000", color: "#fff" }}
                        data-testid={`badge-new-${product.id}`}>
                    {lang === "ar" ? "جديد" : "New"}
                  </span>
                )}
                {isExpress && (
                  <span className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold shadow-sm border border-white/30 backdrop-blur"
                        style={{ background: "rgba(0,0,0,0.75)", color: "#fff" }}
                        data-testid={`badge-express-${product.id}`}>
                    <Zap size={10} fill="currentColor" /> {lang === "ar" ? "خلال ساعة" : "1-hour"}
                  </span>
                )}
              </>
            )}
          </div>

          {/* RG brand watermark — bottom-start */}
          <span className="absolute bottom-2 start-2 flex items-center justify-center rounded-full w-7 h-7 shadow-sm pointer-events-none"
                style={{ background: "rgba(6,15,12,0.5)", backdropFilter: "blur(4px)" }}
                data-testid={`product-rg-watermark-${product.id}`}>
            <img src={BRAND.logoWhite} alt="RG" className="w-[18px] h-[18px] object-contain" loading="lazy" />
          </span>

          {/* Actions — top-end, fixed vertical stack (heart + share) */}
          <div className="absolute top-2.5 end-2.5 flex flex-col items-center gap-1.5">
            <button
              className="w-8 h-8 rounded-full backdrop-blur flex items-center justify-center transition-all active:scale-90 shadow-sm"
              style={{
                background: fav ? "#e11d48" : "rgba(255,255,255,0.92)",
                color: fav ? "#fff" : "var(--primary)",
              }}
              onClick={handleFav}
              data-testid={`product-favorite-${product.id}`}
              aria-label={fav ? "Remove from favorites" : "Add to favorites"}
            >
              <Heart size={15} fill={fav ? "currentColor" : "none"} />
            </button>
            <ShareButton product={product} variant="icon" />
          </div>
        </div>

        <div className="p-2.5 sm:p-3">
          <div className="text-[13px] sm:text-sm font-medium line-clamp-2 leading-snug min-h-[2.4em]"
               style={{ color: "var(--text-main)" }}>
            {name}
          </div>
          {isCommunity && product.designer_name && (
            <div className="text-[11px] mt-0.5 opacity-60 line-clamp-1" data-testid={`product-designer-${product.id}`}>
              {lang === "ar" ? "بواسطة" : "by"} {product.designer_name}
            </div>
          )}
          <div className="flex items-center justify-between mt-2 gap-2">
            <div className="min-w-0">
              <div className="price-display text-base leading-none">
                {Number(product.price_sar) <= 0 && Number(product.options_min_price) > 0
                  ? (lang === "ar" ? `يبدأ من ${formatPrice(product.options_min_price)}` : `From ${formatPrice(product.options_min_price)}`)
                  : formatPrice(product.price_sar)}
              </div>
              {hasDiscount && (
                <div className="text-[11px] line-through opacity-50 mt-0.5" data-testid={`price-old-${product.id}`}>
                  {formatPrice(product.price_previous)}
                </div>
              )}
              {showBnplHint && (
                <div className="text-[10px] mt-1 font-bold leading-none" style={{ color: "#0B2A5B" }}
                     data-testid={`bnpl-hint-${product.id}`}>
                  {lang === "ar"
                    ? `🗓️ قسّطها من ${formatPrice(cardPrice / 4)}`
                    : `🗓️ Split from ${formatPrice(cardPrice / 4)}`}
                </div>
              )}
            </div>
            {unavailable ? (
              <span className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center opacity-60"
                    style={{ background: "#f59e0b22", color: "#b45309" }}
                    data-testid={`product-blocked-bell-${product.id}`}
                    aria-label="unavailable">
                🔔
              </span>
            ) : (
            <button
              onClick={handleAdd}
              className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center transition-all hover:scale-105"
              style={{ background: "var(--primary)", color: "#fff" }}
              data-testid={`product-add-${product.id}`}
              aria-label={t("addToCart")}
            >
              <Plus size={16} />
            </button>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export default memo(ProductCard, (prev, next) =>
  prev.product?.id === next.product?.id &&
  prev.product?.image === next.product?.image &&
  prev.product?.price_sar === next.product?.price_sar &&
  prev.product?.price_previous === next.product?.price_previous &&
  prev.product?.is_new === next.product?.is_new &&
  prev.product?.featured === next.product?.featured &&
  prev.product?.blocked === next.product?.blocked &&
  prev.product?.source === next.product?.source &&
  prev.product?.designer_name === next.product?.designer_name &&
  prev.product?.name_ar === next.product?.name_ar &&
  prev.product?.name_en === next.product?.name_en
);
